import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import GoogleTagLifecycle from "./components/GoogleTagLifecycle";
import PostHogLifecycle from "./components/PostHogLifecycle";
import SentryUserLifecycle from "./components/SentryUserLifecycle";
import AffiliateAttributionLifecycle from "./components/AffiliateAttributionLifecycle";
import OAuthRedirectGuard from "./components/OAuthRedirectGuard";
import { SIGNUPS_ENABLED } from "./config/auth";

// ── Public pages ──────────────────────────────────────────────────────────────
const Index              = lazy(() => import("./pages/Index"));
const HowItWorks         = lazy(() => import("./pages/HowItWorks"));
const Features           = lazy(() => import("./pages/Features"));
const About              = lazy(() => import("./pages/About"));
const Contact            = lazy(() => import("./pages/Contact"));
const Examples           = lazy(() => import("./pages/Examples"));
const Pricing            = lazy(() => import("./pages/Pricing"));
const ROICalculator      = lazy(() => import("./pages/ROICalculator"));
const Login              = lazy(() => import("./pages/Login"));
const Register           = lazy(() => import("./pages/Register"));
const NotFound           = lazy(() => import("./pages/NotFound"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const PrivacyPolicy      = lazy(() => import("./pages/PrivacyPolicy"));
const CookiesPolicy      = lazy(() => import("./pages/CookiesPolicy"));
const ModelPage          = lazy(() => import("./pages/ModelPage"));
const AIVideo            = lazy(() => import("./pages/AIVideo"));
const AIImage            = lazy(() => import("./pages/AIImage"));
const UGCStudio          = lazy(() => import("./pages/UGCStudio"));
const Services           = lazy(() => import("./pages/Services"));
const AudioPage          = lazy(() => import("./pages/AudioPage"));
const Seedance2          = lazy(() => import("./pages/Seedance2"));
const BetaHolding        = lazy(() => import("./pages/BetaHolding"));
const AuthCallback       = lazy(() => import("./pages/AuthCallback"));
const CheckEmail         = lazy(() => import("./pages/CheckEmail"));
const ForgotPassword     = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword      = lazy(() => import("./pages/ResetPassword"));
const CookieConsent      = lazy(() => import("./components/CookieConsent"));
const DashboardRoot      = lazy(() => import("./components/dashboard/DashboardRoot"));

// ── Dashboard pages ───────────────────────────────────────────────────────────
const DashboardHome        = lazy(() => import("./pages/dashboard/DashboardHome"));
const VideoCreation        = lazy(() => import("./pages/dashboard/VideoCreation"));
const FrameToFrame         = lazy(() => import("./pages/dashboard/FrameToFrame"));
const EditVideo            = lazy(() => import("./pages/dashboard/EditVideo"));
const VideoToVideo         = lazy(() => import("./pages/dashboard/VideoToVideo"));
const MotionControl        = lazy(() => import("./pages/dashboard/MotionControl"));
const ImageCreation        = lazy(() => import("./pages/dashboard/ImageCreation"));
const Projects             = lazy(() => import("./pages/dashboard/Projects"));
const ProjectDetail        = lazy(() => import("./pages/dashboard/ProjectDetail"));
const UploadsFolder        = lazy(() => import("./pages/dashboard/UploadsFolder"));
const Billing              = lazy(() => import("./pages/dashboard/Billing"));
const Settings             = lazy(() => import("./pages/dashboard/Settings"));
const Help                 = lazy(() => import("./pages/dashboard/Help"));
const CreateImageToImage   = lazy(() => import("./pages/dashboard/CreateImageToImage"));
const CreateEditImage      = lazy(() => import("./pages/dashboard/CreateEditImage"));
const CreateImageUpscaler  = lazy(() => import("./pages/dashboard/CreateImageUpscaler"));
const CreateVideoUpscaler  = lazy(() => import("./pages/dashboard/CreateVideoUpscaler"));
const CreateCaptions       = lazy(() => import("./pages/dashboard/CreateCaptions"));
const UgcComingSoon        = lazy(() => import("./pages/dashboard/UgcComingSoon"));
const AudioComingSoon      = lazy(() => import("./pages/dashboard/AudioComingSoon"));

// ── Blog pages ────────────────────────────────────────────────────────────────
const BlogIndex              = lazy(() => import("./pages/blog/BlogIndex"));
const MakeUGCAlternative     = lazy(() => import("./pages/blog/MakeUGCAlternative"));
const ArcadsAlternative      = lazy(() => import("./pages/blog/ArcadsAlternative"));
const CreatifyAlternative    = lazy(() => import("./pages/blog/CreatifyAlternative"));
const BestAIUGCVideoGenerator  = lazy(() => import("./pages/blog/BestAIUGCVideoGenerator"));
const HeyGenAlternative      = lazy(() => import("./pages/blog/HeyGenAlternative"));
const BestAIVideoGenerator2025 = lazy(() => import("./pages/blog/BestAIVideoGenerator2025"));
const HeyGenVsSynthesia      = lazy(() => import("./pages/blog/HeyGenVsSynthesia"));
const ArcadsVsCreatify       = lazy(() => import("./pages/blog/ArcadsVsCreatify"));
const HowToUseSora2          = lazy(() => import("./pages/blog/HowToUseSora2"));
const AIProductShotGenerator = lazy(() => import("./pages/blog/AIProductShotGenerator"));
const Kling3Review           = lazy(() => import("./pages/blog/Kling3Review"));
const Veo3Review             = lazy(() => import("./pages/blog/Veo3Review"));
const MakeUGCVsCreatify      = lazy(() => import("./pages/blog/MakeUGCVsCreatify"));
const AIAvatarGenerator      = lazy(() => import("./pages/blog/AIAvatarGenerator"));
const SynthesiaAlternative   = lazy(() => import("./pages/blog/SynthesiaAlternative"));
const AIProductDemoVideo     = lazy(() => import("./pages/blog/AIProductDemoVideo"));
const TextToVideoAI          = lazy(() => import("./pages/blog/TextToVideoAI"));
const FLUXKontextMaxReview   = lazy(() => import("./pages/blog/FLUXKontextMaxReview"));
const Imagen4VsGPTImage      = lazy(() => import("./pages/blog/Imagen4VsGPTImage"));
const NanoBanana2Review      = lazy(() => import("./pages/blog/NanoBanana2Review"));
const Sora2Shutdown              = lazy(() => import("./pages/blog/Sora2Shutdown"));
const Sora2Alternatives          = lazy(() => import("./pages/blog/Sora2Alternatives"));
const KlingVsVeoVsRunway         = lazy(() => import("./pages/blog/KlingVsVeoVsRunway"));
const HowToUseVeo3               = lazy(() => import("./pages/blog/HowToUseVeo3"));
const OpenAISpud                 = lazy(() => import("./pages/blog/OpenAISpud"));
const BestAIVideoGeneratorForAds = lazy(() => import("./pages/blog/BestAIVideoGeneratorForAds"));
const Wan21Review                = lazy(() => import("./pages/blog/Wan21Review"));
const AIUGCAdsGuide              = lazy(() => import("./pages/blog/AIUGCAdsGuide"));
const Flux2ProVsGPTImageVsImagen4 = lazy(() => import("./pages/blog/Flux2ProVsGPTImageVsImagen4"));
const AIProductVideoAds          = lazy(() => import("./pages/blog/AIProductVideoAds"));
const MidjourneyV8VsFlux2Pro     = lazy(() => import("./pages/blog/MidjourneyV8VsFlux2Pro"));
const SeedanceCopyright          = lazy(() => import("./pages/blog/SeedanceCopyright"));
const KlingVsSeedance            = lazy(() => import("./pages/blog/KlingVsSeedance"));
const SeedanceAudioGeneration    = lazy(() => import("./pages/blog/SeedanceAudioGeneration"));
const ReduceVideoProductionCostsAI = lazy(() => import("./pages/blog/ReduceVideoProductionCostsAI"));
const AIVideoMarketingTeams      = lazy(() => import("./pages/blog/AIVideoMarketingTeams"));
const AIVideoForMarketingAgencies = lazy(() => import("./pages/blog/AIVideoForMarketingAgencies"));
const AIUGCAdsForShopify         = lazy(() => import("./pages/blog/AIUGCAdsForShopify"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <SentryUserLifecycle />
      <AffiliateAttributionLifecycle />
      <GoogleTagLifecycle />
      <PostHogLifecycle />
      <OAuthRedirectGuard />
      <ScrollToTop />
      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <Suspense fallback={<div className="min-h-screen bg-background" />}>
        <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/features" element={<Features />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/examples" element={<Examples />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/pay-per-use" element={<Navigate to="/pricing" replace />} />
            <Route path="/pay-per-use/status" element={<Navigate to="/pricing" replace />} />
            <Route path="/pay-per-video" element={<Navigate to="/pricing" replace />} />
            <Route path="/roi-calculator" element={<ROICalculator />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cookies" element={<CookiesPolicy />} />
            <Route path="/login" element={<Login />} />
            <Route
              path="/register"
              element={SIGNUPS_ENABLED ? <Register /> : <Navigate to="/login" replace />}
            />

            <Route path="/services" element={<Services />} />

            {/* Public SEO landing pages */}
            <Route path="/ai-video" element={<AIVideo />} />
            <Route path="/ai-image" element={<AIImage />} />
            <Route path="/ugc-studio" element={<UGCStudio />} />
            <Route path="/audio" element={<AudioPage />} />
            <Route path="/seedance-2" element={<Seedance2 />} />

            {/* Public model info pages */}
            <Route path="/models/:type/:slug" element={<ModelPage />} />

            {/* Post-registration email confirmation screen */}
            <Route path="/check-email" element={<CheckEmail />} />

            {/* Password reset flow */}
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* OAuth callback — processes token and routes to /beta or /dashboard */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Beta holding page — shown to new signups when VITE_BETA_MODE=true */}
            <Route path="/beta" element={<BetaHolding />} />

            {/* Dashboard routes — require authentication */}
            <Route path="/dashboard" element={<DashboardRoot />}>
              <Route index element={<DashboardHome />} />
              <Route path="create/video" element={<VideoCreation />} />
              <Route path="create/frame-to-frame" element={<FrameToFrame />} />
              <Route path="create/edit-video" element={<EditVideo />} />
              <Route path="create/video-to-video" element={<VideoToVideo />} />
              <Route path="create/motion-control" element={<MotionControl />} />
              <Route path="create/image" element={<ImageCreation />} />
              {/* Redirects from old routes */}
              <Route path="create/ugc-video" element={<Navigate to="/dashboard/create/video" replace />} />
              <Route path="create/ugc-frame-layout" element={<Navigate to="/dashboard/create/video" replace />} />
              <Route path="create/kling-o3-edit-video" element={<Navigate to="/dashboard/create/video" replace />} />
              <Route path="create/kling-o3-reference-video" element={<Navigate to="/dashboard/create/video" replace />} />
              <Route path="create/audio" element={<AudioComingSoon />} />
              <Route path="create/avatar" element={<UgcComingSoon />} />
              <Route path="create/captions" element={<CreateCaptions />} />
              <Route path="create/text-to-image" element={<Navigate to="/dashboard/create/image" replace />} />
              <Route path="create/image-to-image" element={<CreateImageToImage />} />
              <Route path="create/edit-image" element={<CreateEditImage />} />
              <Route path="create/image-upscaler" element={<CreateImageUpscaler />} />
              <Route path="create/video-upscaler" element={<CreateVideoUpscaler />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/uploads" element={<UploadsFolder />} />
              <Route path="projects/:projectId" element={<ProjectDetail />} />
              <Route path="storage" element={<Navigate to="/dashboard/projects" replace />} />
              <Route path="billing" element={<Billing />} />
              <Route path="settings" element={<Settings />} />
              <Route path="help" element={<Help />} />
            </Route>

            {/* Blog routes */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/makeugc-alternative" element={<MakeUGCAlternative />} />
            <Route path="/blog/arcads-alternative" element={<ArcadsAlternative />} />
            <Route path="/blog/creatify-alternative" element={<CreatifyAlternative />} />
            <Route path="/blog/best-ai-ugc-video-generator" element={<BestAIUGCVideoGenerator />} />
            <Route path="/blog/heygen-alternative" element={<HeyGenAlternative />} />
            <Route path="/blog/best-ai-video-generator-2025" element={<BestAIVideoGenerator2025 />} />
            <Route path="/blog/heygen-vs-synthesia" element={<HeyGenVsSynthesia />} />
            <Route path="/blog/arcads-vs-creatify" element={<ArcadsVsCreatify />} />
            <Route path="/blog/how-to-use-sora-2" element={<HowToUseSora2 />} />
            <Route path="/blog/ai-product-shot-generator" element={<AIProductShotGenerator />} />
            <Route path="/blog/kling-3-review" element={<Kling3Review />} />
            <Route path="/blog/veo-3-review" element={<Veo3Review />} />
            <Route path="/blog/makeugc-vs-creatify" element={<MakeUGCVsCreatify />} />
            <Route path="/blog/ai-avatar-generator" element={<AIAvatarGenerator />} />
            <Route path="/blog/synthesia-alternative" element={<SynthesiaAlternative />} />
            <Route path="/blog/ai-product-demo-video" element={<AIProductDemoVideo />} />
            <Route path="/blog/text-to-video-ai-generator" element={<TextToVideoAI />} />
            <Route path="/blog/flux-kontext-max-review" element={<FLUXKontextMaxReview />} />
            <Route path="/blog/imagen-4-vs-gpt-image" element={<Imagen4VsGPTImage />} />
            <Route path="/blog/nano-banana-2-review" element={<NanoBanana2Review />} />
            <Route path="/blog/sora-2-shutdown" element={<Sora2Shutdown />} />
            <Route path="/blog/sora-2-alternatives" element={<Sora2Alternatives />} />
            <Route path="/blog/kling-vs-veo-vs-runway" element={<KlingVsVeoVsRunway />} />
            <Route path="/blog/how-to-use-veo-3" element={<HowToUseVeo3 />} />
            <Route path="/blog/openai-spud" element={<OpenAISpud />} />
            <Route path="/blog/best-ai-video-generator-for-ads" element={<BestAIVideoGeneratorForAds />} />
            <Route path="/blog/wan-2-1-review" element={<Wan21Review />} />
            <Route path="/blog/ai-ugc-ads-guide" element={<AIUGCAdsGuide />} />
            <Route path="/blog/flux-2-pro-vs-gpt-image-vs-imagen-4" element={<Flux2ProVsGPTImageVsImagen4 />} />
            <Route path="/blog/ai-product-video-ads" element={<AIProductVideoAds />} />
            <Route path="/blog/midjourney-v8-vs-flux-2-pro" element={<MidjourneyV8VsFlux2Pro />} />
            <Route path="/blog/seedance-2-copyright" element={<SeedanceCopyright />} />
            <Route path="/blog/kling-3-vs-seedance-2" element={<KlingVsSeedance />} />
            <Route path="/blog/seedance-2-audio-generation" element={<SeedanceAudioGeneration />} />
            <Route path="/blog/reduce-video-production-costs-ai" element={<ReduceVideoProductionCostsAI />} />
            <Route path="/blog/ai-video-marketing-teams" element={<AIVideoMarketingTeams />} />
            <Route path="/blog/ai-video-for-marketing-agencies" element={<AIVideoForMarketingAgencies />} />
            <Route path="/blog/ai-ugc-ads-shopify" element={<AIUGCAdsForShopify />} />

            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
